/**
 * editorCommands.ts — die einzige Stelle im RichTextEditor, die
 * `document.execCommand` aufruft.
 *
 * `execCommand` ist zwar als deprecated markiert, aber in allen Zielbrowsern
 * funktionsfähig und die einzige Möglichkeit, ohne eine schwere Editor-Library
 * (ProseMirror, Slate, …) formatierten Text in einem `contentEditable` zu
 * erzeugen. Die Kapselung hier hält die Deprecation an einem Ort und macht die
 * Kommandos einzeln testbar.
 */

/** Auswahl der Schriftarten in der Werkzeugleiste (Reihenfolge = Anzeige). */
import { aktiveAuswahl, aktivesDokument } from "../../windowManager/browserFenster/fensterRegister";
export const FONT_FAMILIES = ["Times New Roman", "Segoe UI", "Arial", "Courier New"];

/** Standardschrift des Editors (= Sende-Schrift, siehe `sendBodyFont.ts`). */
export const EDITOR_DEFAULT_FONT = "Times New Roman";

/** Standardgröße des Editors in pt (= Sende-Schrift). */
export const EDITOR_DEFAULT_SIZE_PT = 11;

/** Vollständiger Font-Stack der Standardschrift (Editor-Container und Sende-Schrift). */
export const EDITOR_DEFAULT_FONT_STACK = `'${EDITOR_DEFAULT_FONT}', Times, serif`;

/** Schriftgrößen in pt: 8 bis 36 in Einerschritten. */
export const FONT_SIZES = Array.from({ length: 29 }, (_, i) => i + 8);

/**
 * Führt ein Editor-Kommando aus.
 *
 * Wichtig ist die Reihenfolge: erst fokussieren, dann das Kommando. Umgekehrt
 * liefe `execCommand` gegen ein nicht fokussiertes Element und bliebe wirkungslos.
 */
export function execCmd(root: HTMLElement | null, cmd: string, value?: string): void {
    root?.focus();
    // eslint-disable-next-line no-restricted-syntax
    aktivesDokument().execCommand(cmd, false, value);
}

/**
 * Setzt eine punktgenaue Schriftgröße.
 *
 * `execCommand("fontSize")` kennt nur die alte HTML-Skala 1–7. Der Trick: Größe
 * 7 als Marker anwenden und die dadurch erzeugten `<font size="7">` anschließend
 * auf die echte pt-Größe umschreiben. Das entstehende `style="font-size:…"`
 * überlebt den Mail-Sanitizer (`font[style]` ist erlaubt) und erreicht so den
 * Empfänger.
 */
export function applyFontSize(root: HTMLElement | null, pt: number): void {
    if (!root) return;
    root.focus();
    // eslint-disable-next-line no-restricted-syntax
    aktivesDokument().execCommand("fontSize", false, "7");
    root.querySelectorAll('font[size="7"]').forEach((el) => {
        const f = el as HTMLElement;
        f.removeAttribute("size");
        f.style.fontSize = `${pt}pt`;
    });
}

/**
 * „Formatierung löschen": setzt die Markierung auf die Editor-Standardschrift
 * zurück. `removeFormat` entfernt nur Zeichenformat (`font`, `b`, `i`, `u`,
 * `span`-Styles …) — Blöcke wie der Signatur- oder Zitat-Container bleiben samt
 * ihren `data-*`-Markern stehen.
 */
export function clearFormat(root: HTMLElement | null): void {
    execCmd(root, "removeFormat");
}

/** Schriftname, den {@link umhuelleAuswahl} kurzzeitig als Markierung setzt. */
const AUSWAHL_MARKER = "kmvs-auswahl-marker";

/**
 * Umhüllt die aktuelle Markierung mit eigenen Elementen und gibt sie zurück.
 *
 * Gleicher Trick wie bei {@link applyFontSize}: `fontName` mit einem
 * Marker-Namen anwenden — der Browser zerlegt die Markierung dabei sauber an
 * Block- und Elementgrenzen und legt um jeden markierten Textlauf ein
 * `<font face>`. Den Marker räumen wir sofort wieder ab; zurück bleiben leere
 * `<font>`-Hüllen, auf die der Aufrufer Inline-Styles setzt. Ein Inline-Style
 * am innersten Element gewinnt immer — auch gegen eine Schrift, die von einem
 * Eltern-Block (z. B. eingefügtes `<div style="font-family: …">`) vererbt wird.
 */
export function umhuelleAuswahl(root: HTMLElement | null): HTMLElement[] {
    if (!root) return [];
    root.focus();
    // eslint-disable-next-line no-restricted-syntax
    aktivesDokument().execCommand("fontName", false, AUSWAHL_MARKER);
    const huellen: HTMLElement[] = [];
    root.querySelectorAll("font[face], [style*='font-family']").forEach((el) => {
        const h = el as HTMLElement;
        if (h.getAttribute("face") === AUSWAHL_MARKER) {
            h.removeAttribute("face");
            huellen.push(h);
        } else if (h.style.fontFamily.replace(/["']/g, "").trim() === AUSWAHL_MARKER) {
            // Falls der Browser per CSS statt per <font face> formatiert.
            h.style.removeProperty("font-family");
            huellen.push(h);
        }
    });
    return huellen;
}

/** Fügt HTML an der aktuellen Cursorposition ein. */
export function insertHtmlAtCursor(root: HTMLElement | null, html: string): void {
    if (!root) return;
    root.focus();
    // eslint-disable-next-line no-restricted-syntax
    aktivesDokument().execCommand("insertHTML", false, html);
}

export type RangeKeeper = {
    /** Merkt sich die aktuelle Auswahl — aber nur, wenn sie gültig und im Editor liegt. */
    save(): void;
    /** Stellt die zuletzt gemerkte Auswahl wieder her. */
    restore(): void;
};

/**
 * Bewahrt die Textauswahl über einen Fokuswechsel hinweg.
 *
 * Gebraucht für die nativen `<select>` der Werkzeugleiste: Bei einem Button
 * genügt `onMouseDown={e => e.preventDefault()}`, damit der Fokus im Editor
 * bleibt. Ein `<select>` darf das nicht — sonst öffnet sich das Dropdown nicht.
 * Also wird die Auswahl beim `mousedown` gemerkt und unmittelbar vor dem
 * Kommando wiederhergestellt.
 *
 * Der Kern ist, wann NICHT gespeichert wird: Ist die Auswahl leer oder liegt sie
 * außerhalb des Editors (etwa weil der Fokus schon im Dropdown ist), bleibt der
 * zuvor gemerkte Wert erhalten. Würde man ihn überschreiben, ginge die
 * ursprüngliche Markierung genau dann verloren, wenn man sie braucht.
 */
export function makeRangeKeeper(getRoot: () => HTMLElement | null): RangeKeeper {
    let saved: Range | null = null;
    return {
        save() {
            const sel = typeof window !== "undefined" ? aktiveAuswahl() : null;
            const root = getRoot();
            if (!sel || sel.rangeCount === 0 || !root) return; // nicht überschreiben
            const r = sel.getRangeAt(0);
            if (!root.contains(r.commonAncestorContainer)) return; // nicht überschreiben
            saved = r.cloneRange();
        },
        restore() {
            if (!saved) return;
            const sel = typeof window !== "undefined" ? aktiveAuswahl() : null;
            if (!sel) return;
            sel.removeAllRanges();
            sel.addRange(saved);
        },
    };
}

/** Escaped einen Wert für die Verwendung in einem HTML-Attribut. */
export function escapeAttr(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

/**
 * Gilt der Editor-Inhalt als leer?
 *
 * Ein „leeres" contentEditable enthält je nach Browser `<br>`, `<div><br></div>`
 * oder ein Zero-Width-Space. Die naive Prüfung auf einen leeren String greift
 * deshalb zu kurz.
 */
export function isHtmlEmpty(html: string): boolean {
    return (
        html
            .replace(/<br\s*\/?>/gi, "")
            .replace(/<div>\s*<\/div>/gi, "")
            .replace(/<p>\s*<\/p>/gi, "")
            .replace(/&nbsp;/gi, " ")
            .replace(/[\u200B\uFEFF]/g, "")
            .trim() === ""
    );
}
